import { isNil } from 'ramda'

import {
  ReportAggregationType,
  ReportDateHistogramType,
  ReportFilterOperation,
  ReportMetricType,
  ReportQuery,
} from '@driftt/api-types'

import AggregationBuilder from '../query/AggregationBuilder'
import { IQueryConfig, IQueryOptions } from '../query/types'

import * as Parsers from './parsers'
import ServiceQueryBuilder from './ServiceQueryBuilder'
import { ParserFunction } from './types'

const makeQueries = ({
  startDate,
  endDate,
  sortConfig,
  aggregateType,
  ...queryConfig
}: IQueryConfig & IQueryOptions): {
  [key: string]: ReportQuery
} => {
  return {
    serviceDeflection: new ServiceQueryBuilder()
      .withAggregation(
        new AggregationBuilder(
          'updatedAt',
          ReportAggregationType.DATE_HISTOGRAM
        )
          .withInterval(
            isNil(aggregateType) ? ReportDateHistogramType.DAY : aggregateType
          )
          .withSize(0)
          .withMinMax(startDate, endDate)
          .withMetric('supportConversation.createdAt', ReportMetricType.COUNT)
          .withMetric('supportNotDeflected.createdAt', ReportMetricType.COUNT)
          .build()
      )
      .withFilter('updatedAt', ReportFilterOperation.BETWEEN, [
        startDate,
        endDate,
      ]),
    deflectionRateByPlaybook: new ServiceQueryBuilder()
      .withAggregation(
        new AggregationBuilder('playbookId', ReportAggregationType.TERMS)
          .withInterval(
            isNil(aggregateType) ? ReportDateHistogramType.DAY : aggregateType
          )
          .withSize(0)
          .withMinMax(startDate, endDate)
          .withMetric('supportConversation.createdAt', ReportMetricType.COUNT)
          .withMetric('supportNotDeflected.createdAt', ReportMetricType.COUNT)
          .build()
      )
      .withFilter('updatedAt', ReportFilterOperation.BETWEEN, [
        startDate,
        endDate,
      ]),
    supportConversations: new ServiceQueryBuilder()
      .withAggregation(
        new AggregationBuilder('orgId', ReportAggregationType.TERMS)
          .withInterval(
            isNil(aggregateType) ? ReportDateHistogramType.DAY : aggregateType
          )
          .withSize(0)
          .withMinMax(startDate, endDate)
          .withMetric('supportConversation.createdAt', ReportMetricType.COUNT)
          .withMetric('supportNotDeflected.createdAt', ReportMetricType.COUNT)
          .build()
      )
      .withFilter('updatedAt', ReportFilterOperation.BETWEEN, [
        startDate,
        endDate,
      ]),
    overallCSATRating: new ServiceQueryBuilder()
      .withFilterGroups(queryConfig)
      .withAggregation(
        new AggregationBuilder('orgId', ReportAggregationType.TERMS)
          .withInterval(
            isNil(aggregateType) ? ReportDateHistogramType.DAY : aggregateType
          )
          .withSize(0)
          .withMinMax(startDate, endDate)
          .withDocCount()
          .withMetric('latestLiveChatRating.ratingValue', ReportMetricType.AVG)
          .build()
      )
      .withFilter('updatedAt', ReportFilterOperation.BETWEEN, [
        startDate,
        endDate,
      ]),
    csatByRating: new ServiceQueryBuilder()
      .withFilterGroups(queryConfig)
      .withAggregation(
        new AggregationBuilder(
          'latestLiveChatRating.ratingValue',
          ReportAggregationType.HISTOGRAM
        )
          .withInterval(1)
          .withSize(0)
          .withMinMax(1, 5)

          .withMetric(
            'latestLiveChatRating.ratingValue',
            ReportMetricType.COUNT
          )
          .build()
      )
      .withFilter('updatedAt', ReportFilterOperation.BETWEEN, [
        startDate,
        endDate,
      ]),
    csatByTeammate: new ServiceQueryBuilder()
      .withFilterGroups(queryConfig)
      .withAggregation(
        new AggregationBuilder(
          'latestLiveChatRating.attributedRatingAgentId',
          ReportAggregationType.TERMS
        )
          .withInterval(
            isNil(aggregateType) ? ReportDateHistogramType.DAY : aggregateType
          )
          .withSize(0)
          .withMinMax(startDate, endDate)
          .withDocCount()
          .withMetric('latestLiveChatRating.ratingValue', ReportMetricType.AVG)
          .withMetric('latestLiveChatRating.ratedAt', ReportMetricType.MAX)
          .withSort(sortConfig)
          .build()
      )
      .withFilter('updatedAt', ReportFilterOperation.BETWEEN, [
        startDate,
        endDate,
      ]),
  }
}

const PARSERS_BY_METRIC: Record<string, ParserFunction> = {
  serviceDeflection: Parsers.parseServiceDeflection,
  deflectionRateByPlaybook: Parsers.parseDeflectionRateByPlaybook,
  supportConversations: Parsers.parseSupportConversations,
  overallCSATRating: Parsers.parseOverallCSATRating,
  csatByRating: Parsers.parseCSATByRating,
  csatByTeammate: Parsers.parseCSATByTeammate,
}

export const getQuery = (
  queryConfig: IQueryConfig,
  options?: IQueryOptions
): {
  query: ReportQuery
  parseFunction: ParserFunction
} => {
  const { metric } = queryConfig
  const queriesByMetric = makeQueries({ ...queryConfig, ...options })
  const query = queriesByMetric[metric]

  return {
    query,
    parseFunction: PARSERS_BY_METRIC[metric],
  }
}
